// cleanup_remove_external_libraries_THEME
// cleanup_convert_to_typescript_file_THEME

import { merge, update } from 'lodash';

// https://create-react-app.dev/docs/adding-images-fonts-and-files/
// Webpack will put these in a static folder if you add .png, .svg, etc...
// TODO: potentially remove these styles as they seem to be for old backbone datepicker and grid
import { imageViewDetailsTheme } from 'components/imageViewDetails/imageViewDetail.styles';
import { depositSlipTheme } from 'components/depositSlip/depositSlip.styles';
import { workflowTheme } from 'components/workflow/workflow.styles';
import pageHeader from 'components/pageHeader/PageHeader.styles';
import mobilegrid from 'components/basicMobileGrid/override.styles';
import button from 'components/button/Button.styles';
import formElements from 'components/formElements/formElements.styles';
import dropdownReact from 'components/dropdownReact/dropdownReact.styles';
import formComponents from 'components/formComponents/formComponents.styles';
import { themeOverride as TypeAhead } from 'components/typeAhead/TypeAhead.styles';
import badge from 'components/badge/Badge.styles';
import tabs from 'components/tabs/Tabs.styles';
import sideNav from 'components/sideNav/sideNavTheme.styles';
import { alertTheme } from 'components/alert/Alert.styles';
import { modalNotificationTheme } from 'components/modalNotification/ModalNotification.styles';
import favicon from 'images/favicon.ico';
import fdicLogo from 'images/fdic-logo.png';
import logoSmall from 'images/logoSmall.svg';
import logoLarge from 'images/logoLarge.svg';
import loginLogo from 'images/logo-bottomline.svg';
import loginRightBrand from 'images/login-brand-right.png';

import grid from './Grid.styles';
import readOnlyTheme from './readOnly.styles';
import datePickerDropDown from './datePickerDropdown.styles';
import { defaultBTColor, defaultBTColorDark, defaultBTColorLight } from './colors';

export const baseTheme = {
  title: 'Bottomline Technologies',
  backgrounds: { default: '#fff' },
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  images: {
    favicon,
    logos: {
      login: loginLogo,
      fdic: fdicLogo,
      header: loginLogo,
      sideNav: {
        large: logoLarge,
        small: logoSmall
      }
    },
    login: {
      marketing: loginRightBrand
    }
  },
  login: {
    form: {}
  },
  shadows: {
    normal: '#D3D3D3'
  },
  palette: {
    background: '#FFFFFF',
    text: {
      dark: '#333333',
      light: '#FFFFFF'
    },
    common: {
      black: '#000',
      white: '#FFFFFF',
      type: 'light'
    },
    primary: {
      dark: '#003366',
      light: '#20a2ff',
      normal: '#0070b9',
      contrastText: '#337ab7',
      darkBackground: '#37435C'
    },
    secondary: {
      light: '#ff4081',
      normal: '#ccc',
      dark: '#c51162',
      contrastText: '#31708f'
    },
    success: {
      light: '#DFF0D6',
      normal: '#A6E400',
      dark: '#8CB566',
      contrastText: '#3c763d',
      darker: '#669933'
    },
    positive: {
      light: '#DFF0D6',
      normal: '#A6E400',
      dark: '#8CB566',
      contrastText: '#3c763d'
    },
    warning: {
      light: '#fff7d7',
      normal: '#ffc107',
      dark: '#fad335',
      contrastText: '#8a6d3b'
    },
    info: {
      light: '#E8F3FF',
      normal: '#00DFEB',
      dark: '#4C89C6',
      contrastText: '#31708f',
      lightBlue: '#00dfeb'
    },
    error: {
      light: '#fecccd',
      normal: '#df0000',
      dark: '#df0000',
      contrastText: '#a94442',
      lightOrange: '#f15a24'
    },
    grey: {
      G50: '#fafafa',
      G100: '#f4f7fA',
      G200: '#eeeeee',
      G300: '#e0e0e0',
      G400: '#bdbdbd',
      G500: '#9e9e9e',
      G600: '#6E6E6E',
      G700: '#616161',
      G800: '#424242',
      G900: '#212121',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161'
    }
  },
  nav: {
    color: '#ffffff',
    flyOutBackground: '#14427a',
    activeHighlightColor: '#4dc5d7',
    activeFlyOutBackground: '',
    direction: 'to bottom',
    collapsed: {
      direction: 'to bottom',
      gradientStart: defaultBTColorLight,
      gradientEnd: defaultBTColorDark,
      toggleButtonColor: '#ffffff'
    },
    expanded: {
      direction: 'to bottom',
      gradientStart: defaultBTColorLight,
      gradientEnd: defaultBTColorDark,
      toggleButtonColor: ''
    }
  },
  sizing: {
    sideNav: 68
  },
  spacing: {
    unit: 8
  },
  text: {
    primary: 'rgba(0, 0, 0, 1)',
    secondary: 'rgba(0, 0, 0, 0.54)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)'
  },
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)'
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", "sans-serif"',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightHeavy: 700,
    title: {
      color: '#000000',
      fontSize: 16,
      fontWeight: 300,
      fontFamily: '"Roboto", "Helvetica", "Arial", "sans-serif"'
    },
    mainTitle: {
      fontFamily:
        '"G-Barlow Condensed-n3", "Barlow Condensed", "Roboto", "Helvetica", "Arial", "sans-serif"',
      fontSize: 28,
      fontWeight: 400
    },
    header: {
      direction: 'to left',
      gradientStart: defaultBTColor,
      gradientEnd: defaultBTColor,
      color: '#ffffff',
      height: '150px'
    },
    headline1: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 30,
      fontWeight: 400
    },
    headline2: {
      fontFamily:
        '"G-Roboto Condensed-n4", "Roboto Condensed", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 20,
      fontWeight: 400,
      textTransform: 'uppercase'
    },
    headline3: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 20,
      fontWeight: 400
    },
    largeLabel: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 16,
      fontWeight: 400,
      textTransform: 'uppercase'
    },
    smallLabel: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 12,
      fontWeight: 400,
      textTransform: 'uppercase'
    },
    linkTitle: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 12,
      fontWeight: 400
    },
    subtitle: {
      fontFamily: '"G-Roboto-n5", "Roboto", "ProximaNova", "Helvetica", "Arial", "sans-serif"',
      fontSize: 18,
      fontWeight: 400
    },
    print: {
      fontFamily: '"Arial", "sans-serif"'
    }
  },
  workflow: {
    header: {
      // if gradients are unset in BFS will return defaults

      height: '80px',
      direction: 'to bottom',
      gradientStart: defaultBTColor,
      gradientEnd: defaultBTColor,
      color: '#ffffff',
      extraHeader: {
        backgroundColor: '#0070b9',
        color: '#fff'
      }
    }
  },
  zIndex: {
    mobileStepper: 1000,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500
  },
  shape: {
    borderRadius: 4
  },
  brandingBar: [
    { color: '#366897' }, // 0%,
    { color: '#578131' }, // 25%,
    { color: '#809fb3' }, // 50%,
    { color: '#43807f' } // 75%,
  ],
  widget: {
    separator: {},
    controlIcon: {}
  }
};

export default function theme(customTheme = {}) {
  const theme = merge(baseTheme, customTheme);
  // Handle situation where `palette.background` is ''
  update(theme, 'palette.background', (v) => v || '#ffffff');
  // Handle situation where `button.tertiary.backgroundColor` is '' or `inherit`, but should be transparent by default
  update(theme, 'button.tertiary.backgroundColor', (v) =>
    v && v !== 'inherit' ? v : 'transparent'
  );
  update(theme, 'buttons.tertiary.backgroundColor', (v) =>
    v && v !== 'inherit' ? v : 'transparent'
  );
  const mergedThemeControls = merge(theme, {
    alert: alertTheme(theme),
    badge: badge(theme),
    button: button(theme),
    buttons: button(theme), // for @glu/data-components
    datePickerDropDown: datePickerDropDown(theme),
    dataComponents: {
      hoverLightColor: theme?.palette?.primary?.darkBackground,
      hoverDarkColor: theme?.palette?.primary?.darkBackground
    },
    dropdownReact: dropdownReact(theme),
    depositSlip: depositSlipTheme(theme),
    mobilegrid: mobilegrid(theme),
    formElements: formElements(theme),
    formComponents: formComponents(theme),
    typeAhead: TypeAhead(theme),
    grid: grid(theme),
    imageViewDetails: imageViewDetailsTheme(theme),
    modalNotification: modalNotificationTheme(theme),
    pageHeader: pageHeader(theme),
    readOnly: readOnlyTheme,
    nav: sideNav(theme),
    tabs: tabs(theme),
    workflow: workflowTheme(theme)
  });
  return mergedThemeControls;
}
